import { useMemo, useState, useEffect, useRef } from 'react';
import { useAuthAdapter } from './useAuthAdapter';
const OWNER_KEY = 'swim-lab:owner-oid';
const GUEST_KEY = 'swim-lab:guest-mode';
export { OWNER_KEY, GUEST_KEY };
export function useAuth() {
    const { isLoaded, isSignedIn, user, signIn, signOut } = useAuthAdapter();
    const [guestMode, setGuestMode] = useState(() => typeof window !== 'undefined' && localStorage.getItem(GUEST_KEY) === 'true');
    const [navOpen, setNavOpen] = useState(false);
    const navRef = useRef(null);
    const accounts = useMemo(() => {
        if (!user)
            return [];
        return [{
                localAccountId: user.id,
                name: user.name,
                username: user.username,
            }];
    }, [user]);
    // Clerk can't resolve isLoaded offline (it needs a network round-trip). so accounts
    // stays empty for a previously signed-in user. Fall back to the locally-persisted
    // owner id in that case rather than treating them as an unauthenticated guest.
    const isAuthenticated = accounts.length > 0
        || (!isLoaded && typeof window !== 'undefined' && !!localStorage.getItem(OWNER_KEY));
    const instance = useMemo(() => ({
        loginRedirect: () => { void signIn(); },
        logoutRedirect: () => { void signOut(); },
    }), [signIn, signOut]);
    const enterGuestMode = () => {
        localStorage.setItem(GUEST_KEY, 'true');
        setGuestMode(true);
    };
    useEffect(() => {
        if (accounts.length === 0 && localStorage.getItem(OWNER_KEY)) {
            localStorage.removeItem(GUEST_KEY);
            setGuestMode(false);
        }
    }, [accounts.length]);
    useEffect(() => {
        if (!navOpen)
            return;
        const handler = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setNavOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [navOpen]);
    const handleAuthClick = () => {
        if (accounts.length > 0) {
            void signOut();
        }
        else {
            void signIn();
        }
    };
    return {
        instance,
        accounts,
        isLoaded,
        isSignedIn,
        isAuthenticated,
        guestMode,
        enterGuestMode,
        navOpen,
        setNavOpen,
        navRef,
        handleAuthClick,
    };
}
